import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Bug,
  Mic,
  MicOff,
  Upload,
  Link,
  Send,
  Loader2,
  X,
  AlertTriangle,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

const BugReportButton = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    screenshot_url: '',
    page_url: '',
    audio_transcription: '',
    priority: 'medium'
  });

  // Update page_url when dialog opens or location changes
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        page_url: window.location.href
      }));
    }
  }, [isOpen, location]);

  // Don't render if not authenticated
  if (!user) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Start recording audio for voice transcription
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Create audio blob and transcribe
        const audioBlob = new Blob(audioChunksRef.current, { 
          type: mediaRecorder.mimeType 
        });
        
        await transcribeAudio(audioBlob);
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      toast.success('Enregistrement démarré. Parlez maintenant...');
      
    } catch (error) {
      console.error('Error starting recording:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Accès au microphone refusé. Veuillez autoriser l\'accès dans les paramètres.');
      } else {
        toast.error('Impossible de démarrer l\'enregistrement audio');
      }
    }
  };

  // Stop recording and trigger transcription
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.info('Transcription en cours...');
    }
  };

  // Transcribe audio using Whisper API
  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', audioBlob, 'recording.webm');
      
      const response = await api.post('/bugs/transcribe-audio', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data?.transcript) {
        // Append transcribed text to description
        setFormData(prev => ({
          ...prev,
          description: prev.description 
            ? `${prev.description} ${response.data.transcript}`
            : response.data.transcript,
          audio_transcription: response.data.transcript
        }));
        toast.success('Transcription réussie !');
      } else {
        toast.error('Aucune transcription reçue');
      }
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error('Erreur lors de la transcription. Réessayez ou tapez votre description.');
    } finally {
      setIsTranscribing(false);
    }
  };

  // Handle PNG screenshot upload
  const handleScreenshotUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check if file is PNG
    if (!file.type.includes('png')) {
      toast.error('Seuls les fichiers PNG sont acceptés');
      e.target.value = '';
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux (max 5 Mo)');
      e.target.value = '';
      return;
    }

    setUploadingScreenshot(true);

    // Preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setScreenshotPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const res = await api.post('/upload/image', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      handleInputChange('screenshot_url', res.data.url);
      toast.success('Capture d\'écran ajoutée');
    } catch (error) {
      console.error('Error uploading screenshot:', error);
      toast.error('Erreur lors de l\'upload. La capture est conservée localement.');
    } finally {
      setUploadingScreenshot(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Veuillez donner un titre au bug');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Veuillez décrire le bug');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/bugs/', {
        ...formData,
        screenshot_url: formData.screenshot_url || screenshotPreview
      });
      
      toast.success('Bug signalé avec succès ! Merci pour votre retour.');
      setIsOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error submitting bug:', error);
      toast.error('Erreur lors du signalement');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      screenshot_url: '',
      page_url: window.location.href,
      audio_transcription: '',
      priority: 'medium'
    });
    setScreenshotPreview(null);
  };

  const priorityConfig = {
    low: { label: 'Faible', color: 'bg-slate-100 text-slate-700' },
    medium: { label: 'Moyen', color: 'bg-amber-100 text-amber-700' },
    high: { label: 'Élevé', color: 'bg-orange-100 text-orange-700' },
    critical: { label: 'Critique', color: 'bg-red-100 text-red-700' }
  };

  // Bug button is now hidden for all users - accessed via FloatingWidget menu
  // Keep the dialog accessible via data-testid
  const showFloatingButton = false;

  return (
    <>
      {/* Bug Report Button - Hidden, accessed via menu */}
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          "fixed bottom-6 left-6 z-50 w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center justify-center group",
          !showFloatingButton && "hidden"
        )}
        title="Signaler un bug"
        data-testid="bug-report-button"
      >
        <Bug className="w-5 h-5" />
        <span className="absolute left-full ml-3 px-3 py-1.5 bg-slate-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
          Signaler un bug
        </span>
      </button>

      {/* Bug Report Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader className="pb-2">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Bug className="w-5 h-5 text-red-500" />
              Signaler un bug
            </DialogTitle>
            <DialogDescription className="text-sm">
              Aidez-nous à améliorer MSC Manager en signalant les problèmes rencontrés
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="bug-title">Titre du bug *</Label>
              <Input
                id="bug-title"
                placeholder="Ex: Le bouton de validation ne fonctionne pas"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                data-testid="bug-title-input"
              />
            </div>

            {/* Description with Voice Recording */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="bug-description">Description *</Label>
                <Button
                  type="button"
                  variant={isRecording ? "destructive" : "outline"}
                  size="sm"
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isTranscribing}
                  className="h-8"
                  data-testid="voice-record-button"
                >
                  {isTranscribing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      Transcription...
                    </>
                  ) : isRecording ? (
                    <>
                      <MicOff className="w-4 h-4 mr-1" />
                      Arrêter
                    </>
                  ) : (
                    <>
                      <Mic className="w-4 h-4 mr-1" />
                      Dicter
                    </>
                  )}
                </Button>
              </div>
              <Textarea
                id="bug-description"
                placeholder="Décrivez le bug en détail : ce que vous faisiez, ce qui s'est passé, ce qui aurait dû se passer..."
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                className="resize-none"
                data-testid="bug-description-input"
              />
              {isRecording && (
                <div className="flex items-center gap-2 text-red-500 text-sm animate-pulse">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-ping" />
                  Enregistrement en cours... Parlez maintenant
                </div>
              )}
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <Label>Priorité</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => handleInputChange('priority', value)}
              >
                <SelectTrigger data-testid="bug-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(priorityConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <Badge className={cn("text-xs", config.color)}>
                          {config.label}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Screenshot (PNG only) */}
            <div className="space-y-2">
              <Label>Capture d'écran (PNG uniquement)</Label>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png"
                  onChange={handleScreenshotUpload}
                  className="hidden"
                  data-testid="screenshot-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingScreenshot}
                  className="flex-1"
                  data-testid="upload-screenshot-button"
                >
                  {uploadingScreenshot ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Ajouter un fichier PNG
                </Button>
              </div>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                Format accepté : PNG uniquement (max 5 Mo)
              </p>
              {screenshotPreview && (
                <div className="relative mt-2">
                  <img
                    src={screenshotPreview}
                    alt="Screenshot preview"
                    className="w-full h-32 object-cover rounded-lg border"
                  />
                  <button
                    onClick={() => {
                      setScreenshotPreview(null);
                      handleInputChange('screenshot_url', '');
                    }}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    data-testid="remove-screenshot-button"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>

            {/* Page URL */}
            <div className="space-y-2">
              <Label htmlFor="bug-url">Lien de la page concernée</Label>
              <div className="flex items-center gap-2">
                <Link className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <Input
                  id="bug-url"
                  value={formData.page_url}
                  onChange={(e) => handleInputChange('page_url', e.target.value)}
                  placeholder="https://..."
                  className="flex-1 text-sm bg-slate-50"
                  readOnly
                  data-testid="bug-url-input"
                />
              </div>
              <p className="text-xs text-slate-500">L'URL de la page actuelle est pré-remplie automatiquement</p>
            </div>

            {/* Info Banner */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                Vos informations de profil seront associées à ce signalement pour faciliter le suivi.
              </p>
            </div>
          </div>

          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsOpen(false)} className="w-full sm:w-auto">
              Annuler
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !formData.title || !formData.description || isRecording || isTranscribing}
              className="w-full sm:w-auto bg-red-500 hover:bg-red-600"
              data-testid="submit-bug-button"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Envoyer le signalement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BugReportButton;
